import React, { useMemo, useState } from 'react';
import { getCoreRowModel } from '@tanstack/react-table';

import { Checkbox, CircularProgress, FormControlLabel, Paper } from '@mui/material';

import { addPolicyToRole, removePolicyFromRole, setRoleInternalFlag } from '../../../../services/role.service';
import { exportPolicies, getPolicies } from '../../../../services/policy.service';

import { useNotifyMessage } from '../../../../hooks/useNotifyMessage';

import SearchBar from '../../../common/SearchBar/SearchBar';
import { Table, useStandardTable as useTable } from '../../../common/table';

import { Role } from '../../../../types/Role';

import makeRolePolicyColumn from './makeRolePolicyColumn';
import InternalUserConfirmDialog from '../appuser/InternalUserConfirmDialog';

export default function RolePolicies(props: { role: Role; setRole: (role: Role) => void }) {
  const [paramFilter, setParamFilter] = useState<any>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingInternalValue, setPendingInternalValue] = useState<boolean | null>(null);
  const [isUpdatingInternalFlag, setIsUpdatingInternalFlag] = useState(false);
  const { notifyMessage } = useNotifyMessage();

  const handleChange = async (event, policyId) => {
    if (event.target.checked) {
      const response = await addPolicyToRole(props.role.id, policyId);

      if (response.status === 200) {
        props.setRole(response.data);
        notifyMessage('Policy added to role', 'success');
        return;
      }

      notifyMessage('FAILED to add policy to role', 'error');
      return;
    }

    const response = await removePolicyFromRole(props.role.id, policyId);

    if (response.status === 200) {
      props.setRole(response.data);
      notifyMessage('Policy removed from role', 'success');
      return;
    }

    notifyMessage('FAILED to remove policy from role', 'error');
  };

  const onInternalCheckboxClick = () => {
    setPendingInternalValue(!props.role.makesUsersInternalUsers);
    setConfirmOpen(true);
  };

  const columns = useMemo(() => makeRolePolicyColumn(props.role, handleChange), [props.role]);

  const table = useTable<any>({
    columns,
    accessKey: 'id',
    fetchAsync: getPolicies,
    exportAsync: exportPolicies,
    params: paramFilter,
    serverSide: true,
  });

  const onSearchChange = (filters: any) => {
    setParamFilter({
      filters: filters,
    });
  };

  const onConfirmInternalToggle = async () => {
    if (pendingInternalValue === null) return;

    setConfirmOpen(false);
    setIsUpdatingInternalFlag(true);

    try {
      const response = await setRoleInternalFlag(props.role.id, pendingInternalValue);

      if (response.status === 200) {
        props.setRole(response.data);
        notifyMessage(`Role is now ${pendingInternalValue ? 'Internal' : 'External'}`, 'success');
      } else {
        notifyMessage('FAILED to update role internal flag', 'error');
      }
    } catch (err: any) {
      notifyMessage(err?.toString() || 'Something went wrong', 'error');
    } finally {
      setIsUpdatingInternalFlag(false);
      setPendingInternalValue(null);
    }
  };

  const onCancelInternalToggle = () => {
    setConfirmOpen(false);
    setPendingInternalValue(null);
  };

  return (
    <>
      <h2>Policies</h2>
      <React.Fragment>
        <SearchBar onSearch={onSearchChange} columns={columns} allowSearchAll={true} />
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 8 }}>
          <FormControlLabel
            label='Only for internal users'
            control={<Checkbox checked={!!props.role.makesUsersInternalUsers} disabled={isUpdatingInternalFlag} onChange={onInternalCheckboxClick} />}
            sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
          />
          {isUpdatingInternalFlag && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </div>
        <Paper>
          <Table {...table} showSearchBar={false} showColumnToggler={false} showAction={true} getCoreRowModel={getCoreRowModel()} />
        </Paper>
        <InternalUserConfirmDialog
          open={confirmOpen}
          makingInternal={!!pendingInternalValue}
          target='role'
          onConfirm={onConfirmInternalToggle}
          onCancel={onCancelInternalToggle}
        />
      </React.Fragment>
    </>
  );
}
